/**
 * Premium "Logo Orbit Launch" intro.
 *
 * Visual design:
 *  - Logo center, two orbital ellipses around it. A "comet" satellite races
 *    along the outer orbit leaving a tapered glowing trail; a smaller
 *    satellite tracks the inner orbit at a different speed.
 *  - Tilted perspective gives the orbits a 3D feel.
 *  - On launch the comet completes a half-revolution and the headline
 *    snaps in.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, brandLogoImg, clipOutroFade, combineSceneFragments, escapeHtml,
  makeTl, typeSize, weights, withAlpha, colors as themeColors,
} from "../../reel/sceneShared.js";

export const logoOrbitLaunch: SceneBuilder = ({
  ctx, clipPrefix, inputs, clipDurationSec, clipStartSec,
}) => {
  const accent = accentFor(inputs);
  const isVertical = ctx.format === "9:16";
  const headline = inputs.text ?? "Allyver hebt ab";
  const sub = inputs.subtext ?? "";
  const headSize = typeSize(isVertical ? "hero" : "mega", ctx.format) * 0.6;
  const subSize = typeSize("md", ctx.format) * 0.55;
  const eyeSize = typeSize("sm", ctx.format) * 0.7;
  const logoH = isVertical ? 200 : 160;
  const orbitOuter = isVertical ? 520 : 440;
  const orbitInner = isVertical ? 340 : 290;

  const t = makeTl(clipStartSec);
  const wrapId = `${clipPrefix}-wrap`;
  const stageId = `${clipPrefix}-stage`;
  const orbit1Id = `${clipPrefix}-orb1`;
  const orbit2Id = `${clipPrefix}-orb2`;
  const cometWrapId = `${clipPrefix}-cwrap`;
  const cometId = `${clipPrefix}-comet`;
  const sat2WrapId = `${clipPrefix}-s2wrap`;
  const sat2Id = `${clipPrefix}-sat2`;
  const logoId = `${clipPrefix}-logo`;
  const haloId = `${clipPrefix}-halo`;
  const headId = `${clipPrefix}-head`;
  const subId = `${clipPrefix}-sub`;
  const eyeId = `${clipPrefix}-eye`;

  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "cinematic", accent });

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;overflow:hidden;">
  <div style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:${isVertical ? 36 : 28}px;padding:96px;">
    <div id="${eyeId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${eyeSize}px;letter-spacing:8px;text-transform:uppercase;color:${accent};opacity:0;">— Mission · Launch —</div>

    <div id="${stageId}" style="position:relative;width:${orbitOuter}px;height:${orbitOuter}px;display:flex;align-items:center;justify-content:center;transform:perspective(1200px) rotateX(58deg) rotateZ(-12deg);">
      <div id="${orbit1Id}" style="position:absolute;inset:0;border-radius:9999px;border:1.5px dashed ${withAlpha(themeColors.fg, 0.32)};box-shadow:inset 0 0 30px ${withAlpha(accent, 0.15)};"></div>
      <div id="${orbit2Id}" style="position:absolute;left:50%;top:50%;width:${orbitInner}px;height:${orbitInner}px;margin-left:-${orbitInner / 2}px;margin-top:-${orbitInner / 2}px;border-radius:9999px;border:1.5px solid ${withAlpha(accent, 0.45)};box-shadow:0 0 18px ${withAlpha(accent, 0.45)};"></div>

      <div id="${cometWrapId}" style="position:absolute;left:50%;top:50%;width:0;height:0;transform-origin:0 0;">
        <div id="${cometId}" style="position:absolute;left:${orbitOuter / 2 - 10}px;top:-10px;width:20px;height:20px;border-radius:9999px;background:${themeColors.fg};box-shadow:0 0 30px ${themeColors.fg}, 0 0 60px ${accent};">
          <div style="position:absolute;right:18px;top:50%;width:120px;height:6px;margin-top:-3px;background:linear-gradient(90deg, transparent 0%, ${withAlpha(accent, 0.7)} 60%, ${withAlpha(themeColors.fg, 0.95)} 100%);border-radius:9999px;filter:blur(2px);"></div>
        </div>
      </div>

      <div id="${sat2WrapId}" style="position:absolute;left:50%;top:50%;width:0;height:0;transform-origin:0 0;">
        <div id="${sat2Id}" style="position:absolute;left:${orbitInner / 2 - 8}px;top:-8px;width:16px;height:16px;border-radius:9999px;background:${accent};box-shadow:0 0 18px ${accent};"></div>
      </div>

      <div id="${haloId}" style="position:absolute;left:50%;top:50%;width:${logoH * 1.7}px;height:${logoH * 1.7}px;margin-left:-${logoH * 0.85}px;margin-top:-${logoH * 0.85}px;border-radius:9999px;background:radial-gradient(circle, ${withAlpha(accent, 0.45)} 0%, transparent 65%);opacity:0;filter:blur(8px);"></div>

      <div id="${logoId}" style="position:relative;filter:drop-shadow(0 12px 30px ${withAlpha(accent, 0.6)});transform:translateZ(40px);">
        ${brandLogoImg({ id: `${logoId}-img`, sizePx: logoH })}
      </div>
    </div>

    <div id="${headId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${headSize}px;line-height:0.95;letter-spacing:-3px;color:${themeColors.fg};text-align:center;text-wrap:balance;max-width:${isVertical ? 920 : 1500}px;text-shadow:0 0 60px ${withAlpha(accent, 0.5)};opacity:0;">${escapeHtml(headline)}</div>
    ${sub ? `<div id="${subId}" style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${subSize}px;letter-spacing:0.4px;color:${withAlpha(themeColors.fg, 0.78)};text-align:center;max-width:${isVertical ? 760 : 1100}px;line-height:1.35;opacity:0;">${escapeHtml(sub)}</div>` : ""}
  </div>
</div>`;

  const init = [
    `gsap.set("#${cometWrapId}", { rotation: -90, transformOrigin: "0 0" });`,
    `gsap.set("#${sat2WrapId}", { rotation: 30, transformOrigin: "0 0" });`,
    `gsap.set("#${logoId}", { opacity: 0, scale: 0.5 });`,
    `gsap.set("#${haloId}", { opacity: 0, scale: 0.6 });`,
    `gsap.set("#${orbit1Id}", { opacity: 0, scale: 0.8 });`,
    `gsap.set("#${orbit2Id}", { opacity: 0, scale: 0.8 });`,
    `gsap.set("#${headId}", { opacity: 0, y: 28 });`,
    sub ? `gsap.set("#${subId}", { opacity: 0, y: 18 });` : "",
    `gsap.set("#${eyeId}", { opacity: 0, y: -16 });`,
  ].filter(Boolean).join("\n");

  const tweens: string[] = [
    t.to(`#${orbit1Id}`, { opacity: 1, scale: 1, duration: 0.6, ease: "raw:\"power2.out\"" }, 0.05),
    t.to(`#${orbit2Id}`, { opacity: 1, scale: 1, duration: 0.6, ease: "raw:\"power2.out\"" }, 0.15),
    t.to(`#${logoId}`, { opacity: 1, scale: 1, duration: 0.6, ease: "raw:\"back.out(1.5)\"" }, 0.25),
    t.to(`#${haloId}`, { opacity: 1, scale: 1, duration: 0.6, ease: "raw:\"power2.out\"" }, 0.3),
    t.to(`#${eyeId}`, { opacity: 1, y: 0, duration: 0.45, ease: "raw:\"power2.out\"" }, 0.4),
    // Comet launches: full revolution.
    t.to(`#${cometWrapId}`, { rotation: 270, duration: 1.2, ease: "raw:\"power2.inOut\"" }, 0.3),
    // Inner sat keeps orbiting throughout.
  ];

  // Continuous orbit during dwell.
  const dwell = Math.max(0.6, clipDurationSec - 1.5);
  tweens.push(t.to(`#${cometWrapId}`, { rotation: 270 + 360, duration: dwell, ease: "raw:\"none\"" }, 1.5));
  tweens.push(t.to(`#${sat2WrapId}`, { rotation: 30 + 720, duration: dwell + 1.0, ease: "raw:\"none\"" }, 0.3));
  tweens.push(t.to(`#${logoId}`, { scale: 1.05, duration: dwell, ease: "raw:\"sine.inOut\"" }, 1.5));

  // Headline + sub.
  tweens.push(t.to(`#${headId}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power3.out\"" }, 1.4));
  if (sub) tweens.push(t.to(`#${subId}`, { opacity: 1, y: 0, duration: 0.5, ease: "raw:\"power2.out\"" }, 1.6));

  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec, 10));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
